import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Layout from './components/Layout';
import Home from './pages/Home';
import SurgicalDivision from './pages/SurgicalDivision';
import Photography from './pages/Photography';
import Videography from './pages/Videography';
import MissionLog from './pages/MissionLog';
import FPV from './pages/FPV';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 z-0">
          <div 
            className="absolute right-0 top-0 w-1/2 h-full opacity-10 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('/bashexo2.png')`,
              maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)'
            }}
          />
        </div>
        
        {/* Grid Pattern */}
        <div className="fixed inset-0 opacity-10 z-0">
          <div 
            className="absolute inset-0 bg-cyber-grid" 
            style={{ backgroundSize: '80px 80px' }}
          />
        </div>

        {/* Scan Line */}
        <div className="scan-line" />

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center relative z-10"
        >
          {/* Logo */}
          <motion.div
            className="flex items-center justify-center space-x-4 mb-8"
            initial={{ y: -50 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.2, type: "spring", damping: 20 }}
          >
            <img 
              src="/br-logo.png" 
              alt="BR Logo" 
              className="w-12 h-12 opacity-80 animate-pulse" 
            />
            <div className="font-orbitron text-white text-4xl font-bold glitch-text cyber-glow-strong" data-text="B.a.s.H">
              B.a.s.H
            </div>
          </motion.div>

          {/* Loading Text */}
          <motion.div 
            className="text-cyber-teal text-lg font-mono mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 2.5, ease: "easeInOut" }}
              className="border-r-2 border-cyber-teal animate-flicker inline-block"
            >
              &gt; booting neural interface...
            </motion.div>
          </motion.div>

          {/* Enhanced Loading Spinner */}
          <div className="flex justify-center space-x-3">
            <motion.div
              animate={{ 
                rotate: 360,
                boxShadow: ["0 0 10px #2dd4bf", "0 0 30px #2dd4bf", "0 0 10px #2dd4bf"]
              }}
              transition={{ 
                rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                boxShadow: { duration: 1, repeat: Infinity }
              }}
              className="w-10 h-10 border-2 border-cyber-teal border-t-transparent rounded-full"
            />
            <motion.div
              animate={{ 
                rotate: -360,
                borderColor: ["#2dd4bf", "#8b5cf6", "#ec4899", "#2dd4bf"]
              }}
              transition={{ 
                rotate: { duration: 1.5, repeat: Infinity, ease: "linear" },
                borderColor: { duration: 2, repeat: Infinity }
              }}
              className="w-8 h-8 border-2 border-cyber-purple border-b-transparent rounded-full"
            />
            <motion.div
              animate={{ 
                rotate: 360,
                scale: [1, 1.2, 1]
              }}
              transition={{ 
                rotate: { duration: 3, repeat: Infinity, ease: "linear" },
                scale: { duration: 1, repeat: Infinity }
              }}
              className="w-6 h-6 border-2 border-cyber-pink border-l-transparent rounded-full"
            />
          </div>

          {/* Status Text */}
          <motion.div
            className="mt-8 text-cyber-gray text-sm font-mono"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            INITIALIZING SYSTEMS...
          </motion.div>
        </motion.div>

        {/* Floating Particles */}
        <div className="fixed inset-0 pointer-events-none z-5">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-cyber-teal rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <Router>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/surgical" element={<SurgicalDivision />} />
            <Route path="/photography" element={<Photography />} />
            <Route path="/videography" element={<Videography />} />
            <Route path="/mission-log" element={<MissionLog />} />
            <Route path="/fpv" element={<FPV />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </Router>
    </HelmetProvider>
  );
}

export default App;
